import { c as _c } from "react/compiler-runtime";
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { Portal } from '@newfront-insurance/core-ui';
import type { AuthProviderContext } from '@newfront-insurance/next-auth';
import { useQuery, useRouter } from '@newfront-insurance/next-router-provider';
import { useProvider, type Provider as ProvisionProvider } from '@newfront-insurance/react-provision';
import { NovaChatContainer, NovaProvider, useNovaWrapperState, type NovaProviderCallbacks } from '../..';
import { FloatingButton } from '../../components/floating-button';
interface ChatOverlayProviderV2Props {
  children: React.ReactNode;
  authProvider: ProvisionProvider<AuthProviderContext>;
  transparentFloatingButton?: boolean;
  callbacks?: NovaProviderCallbacks;
}

/**
 * Simplified chat overlay provider using chat-v2 components.
 * All state management is handled internally by NovaProvider.
 * This wrapper only handles feature flags, portal rendering, and floating button.
 */
export function ChatOverlayProviderV2(t0) {
  const $ = _c(16);
  const {
    children,
    authProvider,
    transparentFloatingButton,
    callbacks
  } = t0;
  const {
    router
  } = useRouter();
  const isChatOverlayEnabled = useFeatureFlag("nova-chat-overlay", false);
  let t1;
  if ($[0] !== router.pathname) {
    t1 = router.pathname.startsWith("/chat");
    $[0] = router.pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isStandaloneMode = t1;
  const {
    userDetails
  } = useProvider(authProvider);
  let t2;
  if ($[2] !== userDetails) {
    t2 = userDetails ?? {};
    $[2] = userDetails;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    uuid: userUuid,
    scopes
  } = t2;
  const queryAccountUuid = useQuery("accountUuid");
  let t3;
  if ($[4] !== authProvider || $[5] !== callbacks || $[6] !== isChatOverlayEnabled || $[7] !== isStandaloneMode || $[8] !== queryAccountUuid || $[9] !== scopes || $[10] !== transparentFloatingButton || $[11] !== userUuid) {
    t3 = isChatOverlayEnabled && userUuid && <Portal name="chat-overlay-portal" portalZIndex={1000}><NovaProvider userUuid={userUuid} scopes={scopes} authProvider={authProvider} isStandaloneMode={isStandaloneMode} initialAccountUuid={queryAccountUuid} callbacks={callbacks}><FloatingButtonWrapper transparentFloatingButton={transparentFloatingButton} /><NovaChatContainer /></NovaProvider></Portal>;
    $[4] = authProvider;
    $[5] = callbacks;
    $[6] = isChatOverlayEnabled;
    $[7] = isStandaloneMode;
    $[8] = queryAccountUuid;
    $[9] = scopes;
    $[10] = transparentFloatingButton;
    $[11] = userUuid;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== children || $[14] !== t3) {
    t4 = <>{children}{t3}</>;
    $[13] = children;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}

/**
 * Wrapper component to render FloatingButton using Nova state.
 * Must be rendered inside NovaProvider.
 */
function FloatingButtonWrapper(t0) {
  const $ = _c(4);
  const {
    transparentFloatingButton
  } = t0;
  const {
    isChatVisible,
    isFloatingButtonVisible,
    hasNotifications,
    handleToggle
  } = useNovaWrapperState();
  const {
    router
  } = useRouter();
  const isStandaloneMode = router.pathname.startsWith("/chat");
  if (!isFloatingButtonVisible || isStandaloneMode || isChatVisible) {
    return null;
  }
  let t1;
  if ($[0] !== handleToggle || $[1] !== hasNotifications || $[2] !== transparentFloatingButton) {
    t1 = <FloatingButton onClick={handleToggle} hasNotifications={hasNotifications} isTransparent={transparentFloatingButton} />;
    $[0] = handleToggle;
    $[1] = hasNotifications;
    $[2] = transparentFloatingButton;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}